import {
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './NumberField.scss';
import { MAX_DURATION } from '@utils/constants/enums';

const AppNumberField: FC<AppNumberFieldProps> = ({
  id,
  label,
  required,
  isRow = false,
  placeholder,
  value: initValue,
  range,
  isDecima = false,
  hideSpinner = false,
  onChange,
  onChangeSize,
  error,
  name,
  icon,
  isPreRegistration = false,
  sx,
}) => {
  const { t } = useTranslation();

  const [value, setValue] = useState<string | undefined>();
  const [isBlur, setIsBlur] = useState<boolean>(false);

  const errorText = useMemo(() => {
    if (error?.type === 'required' && !error.message) {
      return t('common.require_input_text');
    }

    return error?.message ? t(error?.message) : null;
  }, [error]);

  const preventCharacters = useMemo(() => {
    return isDecima ? ['e', 'E', '-', ','] : ['e', 'E', '-', '.', ','];
  }, [isDecima]);

  useEffect(() => {
    setValue(initValue?.toString());
  }, [initValue]);

  useEffect(() => {
    if (!isBlur) return;

    if (value === '' || value === undefined) {
      setValue('');
      onChange?.(undefined);
      setIsBlur(false);
      return;
    }

    const fixedValue = Number(Number(value).toFixed(2));
    setValue(isDecima ? fixedValue.toString() : value);
    onChange?.(Number(fixedValue));
    setIsBlur(false);
  }, [isBlur]);

  const handleIncrease = () => {
    if (!range?.max || Number(value ?? 0) < range.max) {
      const newValue = Number(value ?? 0) + 1;
      setValue(newValue.toString());
      onChange?.(newValue);
    }
  };

  const handleDecrease = () => {
    if (!range?.min || Number(value ?? 0) > range.min) {
      if (value ?? 0) {
        const newValue = Number(value ?? 0) - 1;
        setValue(newValue.toString());
        onChange?.(newValue);
      }
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (name === 'phoneNumber' && value.length > 11) {
      return;
    }
    if (
      (name === 'stayingStart' ||
        name === 'stayingEnd' ||
        name === 'longEntryMin') &&
      +value > MAX_DURATION
    ) {
      setValue(`${MAX_DURATION}`);
      return;
    }
    setValue(value ? value : undefined);
    if (onChangeSize) onChangeSize?.(event);
  };

  const handleBlur = () => {
    if (name === 'phoneNumber') {
      let newValue = value ?? '';
      if (range?.min && Number(newValue) < range.min) {
        newValue = String(range.min);
      } else if (range?.max && Number(newValue) > range.max) {
        newValue = String(range.max);
      }
      setValue(newValue);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onChange?.(newValue as any);
      setIsBlur(false);
      return;
    }

    if (range?.min && Number(value ?? 0) < range.min) {
      setValue(range.min.toString());
    } else if (range?.max && Number(value ?? 0) > range.max) {
      setValue(range.max.toString());
    }

    setIsBlur(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (preventCharacters.includes(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <Grid
      container
      className='NumberField'
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
      sx={sx}
    >
      {label && (
        <Grid
          item
          xs={4}
          alignItems='center'
          justifyContent={isRow ? 'flex-end' : 'flex-start'}
          className={isPreRegistration ? 'pre-label' : ''}
        >
          <FormLabel
            htmlFor={id}
            required={isPreRegistration ? undefined : required}
          >
            {t(label)}
            {isPreRegistration && (
              <Typography className='required'>
                {t('common.required')}
              </Typography>
            )}
          </FormLabel>
        </Grid>
      )}
      <Grid item xs={8}>
        <TextField
          id={id}
          name={id}
          type='number'
          value={value !== undefined ? String(value) : ''}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={t(placeholder!)}
          onPaste={e => e.preventDefault()}
          inputProps={{
            sx: { textAlign: hideSpinner ? 'left' : 'center' },
            step: isDecima ? 'any' : 1,
            ...range,
          }}
          InputProps={{
            startAdornment: !hideSpinner && (
              <InputAction
                position='start'
                content='-'
                onClick={handleDecrease}
              />
            ),
            endAdornment: !hideSpinner ? (
              <InputAction
                position='end'
                content='+'
                onClick={handleIncrease}
              />
            ) : icon ? (
              <InputAdornment position='end'>
                <IconButton
                  className='icon'
                  tabIndex={-1}
                  aria-label='icon-input'
                  edge='end'
                >
                  {icon}
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          error={!!error?.type}
          helperText={errorText}
        />
      </Grid>
    </Grid>
  );
};

interface InputActionProps {
  position: 'start' | 'end';
  content: string;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

const InputAction: FC<InputActionProps> = ({ position, content, onClick }) => (
  <InputAdornment position={position}>
    <IconButton tabIndex={-1} edge={position} onClick={onClick}>
      {content}
    </IconButton>
  </InputAdornment>
);

export default AppNumberField;
